import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../utils/priceFormat';

interface PaymentTypeSectionProps {
  paymentType: 'one_time' | 'subscription';
  setPaymentType: (type: 'one_time' | 'subscription') => void;
  subscriptionType: 'monthly' | 'yearly' | 'trial';
  subscriptionLevel: 'standard' | 'premium';
  premiumPrice: number;
  standardPrice: number;
  disableAutoRenewal?: boolean;
  currencySymbol?: string;
  currency?: string;
}

export const PaymentTypeSection = ({
  paymentType,
  setPaymentType,
  subscriptionType,
  subscriptionLevel,
  premiumPrice,
  standardPrice,
  disableAutoRenewal = false,
  currencySymbol = '฿',
  currency = 'thb'
}: PaymentTypeSectionProps) => {
  const { t } = useTranslation();
  const currentPrice = subscriptionLevel === 'premium' ? premiumPrice : standardPrice;
  const yearlyPrice = currentPrice * 12;
  const subscriptionPrice = subscriptionType === 'yearly' 
    ? yearlyPrice * 0.85  // Combined 15% discount for yearly + subscription
    : currentPrice * 0.95; // 5% discount for monthly + subscription

  // If auto-renewal is disabled and current selection is subscription,
  // force change to one-time payment
  // Also force one-time payment for trial subscriptions
  useEffect(() => {
    if ((disableAutoRenewal || subscriptionType === 'trial') && paymentType === 'subscription') {
      setPaymentType('one_time');
    }
  }, [disableAutoRenewal, subscriptionType, paymentType, setPaymentType]);

  // For trial subscriptions, always disable auto-renewal
  const isAutoRenewalDisabled = disableAutoRenewal || subscriptionType === 'trial';

  return (
    <div className="px-4 py-3">
      <div className="text-sm text-gray-500 mb-2 text-left">{t('subscription.paymentMethods.paymentType')}</div>
      <div className="space-y-2" role="radiogroup" aria-label="Payment type options">
        <button 
          type="button"
          role="radio"
          aria-checked={paymentType === 'one_time'}
          className={`w-full flex items-center justify-between p-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:ring-offset-2 ${
            paymentType === 'one_time' 
              ? 'border-[#F9D348] bg-[#FFF9E6]' 
              : 'bg-white border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setPaymentType('one_time')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPaymentType('one_time');
            }
          }}
          data-payment-type="one_time"
          data-subscription-type={subscriptionType}
          data-subscription-level={subscriptionLevel}
          data-price={subscriptionType === 'yearly' ? yearlyPrice * 0.9 : currentPrice}
          data-billing-period={subscriptionType === 'yearly' ? 'yearly' : 'monthly'}
          data-discount="0"
        >
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              paymentType === 'one_time' ? 'border-[#F9D348]' : 'border-gray-300'
            }`}>
              {paymentType === 'one_time' && (
                <div className="w-2 h-2 rounded-full bg-[#F9D348]"></div>
              )}
            </div>
            <div className="ml-3 text-left">
              <h3 className="text-sm font-medium">{t('subscription.paymentMethods.oneTime')}</h3>
              <p className="text-xs text-gray-500">
                {subscriptionType === 'trial' 
                  ? t('subscription.paymentMethods.oneDeliveryTrial', 'Pay for one trial delivery')
                  : `Pay for ${subscriptionType === 'yearly' ? 'one year' : 'one month'}`
                }
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-sm font-medium">{currencySymbol}{subscriptionType === 'yearly' 
              ? formatPrice(yearlyPrice, currency)
              : formatPrice(currentPrice, currency)}
            </span>
          </div>
        </button>
        
        <button 
          type="button"
          role="radio"
          aria-checked={paymentType === 'subscription'}
          disabled={isAutoRenewalDisabled}
          className={`w-full flex items-center justify-between p-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#F9D348] focus:ring-offset-2 ${
            paymentType === 'subscription' 
              ? 'border-[#F9D348] bg-[#FFF9E6]' 
              : isAutoRenewalDisabled 
                ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                : 'bg-white border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => !isAutoRenewalDisabled && setPaymentType('subscription')}
          onKeyDown={(e) => {
            if (!isAutoRenewalDisabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setPaymentType('subscription');
            }
          }}
          data-payment-type="subscription"
          data-subscription-type={subscriptionType}
          data-subscription-level={subscriptionLevel}
          data-price={subscriptionPrice}
          data-billing-period={subscriptionType === 'yearly' ? 'yearly' : 'monthly'}
          data-discount="5"
          data-auto-renewal="true"
        >
          <div className="flex items-center">
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              paymentType === 'subscription' ? 'border-[#F9D348]' : 'border-gray-300'
            }`}>
              {paymentType === 'subscription' && (
                <div className="w-2 h-2 rounded-full bg-[#F9D348]"></div>
              )}
            </div>
            <div className="ml-3 text-left">
              <h3 className="text-sm font-medium">{t('subscription.paymentMethods.autoRenewal')}</h3>
              <p className="text-xs text-gray-500">{t('subscription.paymentMethods.autoRenewalInfo')}</p>
              {isAutoRenewalDisabled && (
                <p className="text-xs text-gray-500 mt-1">(Not available with PromptPay)</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className="text-sm font-medium">{currencySymbol}{formatPrice(subscriptionPrice, currency)}</span>
            <p className="text-xs text-green-600">{t('subscription.paymentMethods.save5')}</p>
          </div>
        </button>
      </div>
    </div>
  );
};

export default PaymentTypeSection; 